import { OrderEstimateStatus, OrderTermResponse } from '@business/entities/order';
import { InvoiceDiscountType, InvoiceSettingsResponse } from '@business/entities/TransactionInvoice';
import { SettingsConfig } from '@core/utils/config';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { OrderShipmentStatus } from './../../entities/order/OrderShipmentResponse';
import {
  TimeMaterialStatus,
  TMStageType,
  TimeMaterialShippingOption,
  TimeMaterialResponse,
  CompactTimeMaterialResponse,
  ProductStatus,
  TMChangeOrderStatus,
  EstimateLineItem,
  TMFeeType,
} from '@business/entities/tm';
import { TimeMaterialShipmentStatus } from '@business/entities/tm/TimeMaterialShipmentResponse';
import { CompactTimeMaterialRMAResponse, TimeMaterialRMAResponse } from '@business/entities/tm/TimeMaterialRMAResponse';
import { PriceFeeFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay/ProposalProductsOverlay';
import { OrderLineItemData } from '@modules/timeMaterials/AddEditTimeMaterialPage/TimeMaterialForm';
import { ProposalService } from '../proposal_service';
import { ProposalDiscountFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { ProposalDiscountCategory } from '@business/entities/proposal';

export function getStatusTitle(status: TimeMaterialStatus | OrderEstimateStatus | TimeMaterialShipmentStatus) {
  switch (status) {
    case TimeMaterialStatus.open:
      return 'Open';
    case TimeMaterialStatus.completed:
      return 'Completed';
    case TimeMaterialStatus.canceled:
      return 'Canceled';
    case TimeMaterialShipmentStatus.acknowledged:
      return 'Acknowledged';
    case TimeMaterialShipmentStatus.shipped:
      return 'Shipped';
    case TimeMaterialShipmentStatus.delivered:
      return 'Delivered';
    case TimeMaterialShipmentStatus.opened:
      return 'Opened';
  }
  return 'Unknown';
}

export function allStatuses() {
  return [TimeMaterialStatus.open, TimeMaterialStatus.completed, TimeMaterialStatus.canceled];
}

export function partStatuses() {
  const data = [
    ProductStatus.pending,
    ProductStatus.ordered,
    ProductStatus.partial,
    ProductStatus.partialInstalled,
    ProductStatus.received,
    ProductStatus.returned,
    ProductStatus.installed,
    ProductStatus.delivered,
  ];
  return data;
}

export function getPartStatusLabel(status: ProductStatus) {
  switch (status) {
    case ProductStatus.pending:
      return 'Pending';
    case ProductStatus.ordered:
      return 'Ordered';
    case ProductStatus.partial:
      return 'Partial';
    case ProductStatus.partialInstalled:
      return 'Partial Installed';
    case ProductStatus.received:
      return 'Received';
    case ProductStatus.returned:
      return 'Returned';
    case ProductStatus.installed:
      return 'Installed';
    case ProductStatus.delivered:
      return 'Delivered';
  }
}

export function hasShippingInfo(type: TMStageType) {
  return R.includes([TMStageType.acknowledge, TMStageType.delivered, TMStageType.shipped], type);
}
export function allShippingOptions() {
  return [TimeMaterialShippingOption.ready, TimeMaterialShippingOption.complete];
}

export function getShippingOptionTitle(status: TimeMaterialShippingOption) {
  switch (status) {
    case TimeMaterialShippingOption.ready:
      return 'Ship as Ready';
    case TimeMaterialShippingOption.complete:
      return 'Ship as Complete';
  }
  return 'Unknown';
}

export function getShipmentStatusTitle(status: TimeMaterialShipmentStatus) {
  switch (status) {
    case TimeMaterialShipmentStatus.acknowledged:
      return 'Acknowledged';
    case TimeMaterialShipmentStatus.shipped:
      return 'Shipped';
    case TimeMaterialShipmentStatus.opened:
      return 'Opened';
    case TimeMaterialShipmentStatus.delivered:
      return 'Delivered';
  }
  return 'Unknown';
}

export function shipmentStatuses() {
  return [
    TimeMaterialShipmentStatus.opened,
    TimeMaterialShipmentStatus.acknowledged,
    TimeMaterialShipmentStatus.shipped,
    TimeMaterialShipmentStatus.delivered,
  ];
}

export function canCompleteIt(order: TimeMaterialResponse | CompactTimeMaterialResponse, lines: string[] = []) {
  const poItems = R.flatMap(order.purchaseOrders, po => po.items).filter(
    i => R.includes(lines, i.id.toString()) && i.status !== OrderShipmentStatus.delivered,
  );
  if (order.stage.type === TMStageType.delivered) {
    return poItems.length === 0;
  }
  return false;
}

export function getStatusClass(status: TimeMaterialStatus) {
  switch (status) {
    case TimeMaterialStatus.open:
      return 'badge-soft-primary';
    case TimeMaterialStatus.completed:
      return 'badge-soft-success';
    case TimeMaterialStatus.canceled:
      return 'badge-soft-danger';
  }
}

export function getItemStatusClass(status: string) {
  switch (status) {
    case 'closed':
    case 'open':
      return 'badge-primary';
    case 'accepted':
      return 'badge-success';
    case 'rejected':
      return 'badge-danger';
    case 'pending':
      return 'badge-warning';
  }
}

export function getApprovalActionTitle(status: string) {
  switch (status) {
    case 'noCreateInvoice':
      return "Don't create invoice";
    case 'createInvoice':
      return 'Create and Save invoice';
    case 'createSendInvoice':
      return 'Create and Send invoice';
  }
  return status;
}

export function isUMEnabled() {
  const orderSetting = SettingsConfig?.current()?.order;
  if (orderSetting?.isUMEnabled) {
    return true;
  }

  return false;
}

export function isNameEnabled() {
  const orderSetting = SettingsConfig?.current()?.order;
  if (orderSetting?.isUMEnabled) {
    return true;
  }
  return false;
}

export function getSettingAddressString(address: InvoiceSettingsResponse, joinBy = '\n') {
  return R.compact([address.address, `${address.city}, ${address.state} ${address.postal}`, address.country, address.phone]).join(joinBy);
}

export function getDueDateByTerm(term: OrderTermResponse, date: number) {
  let momentDate = toMoment(date).add(term?.fixedDays || 0, 'days');
  if (term?.dayOfMonth && term.dayOfMonth > 0) {
    momentDate = momentDate.set('date', term.dayOfMonth === 31 ? momentDate.daysInMonth() : term.dayOfMonth);
  }

  if (term?.dayOfDueDate && term?.dayOfMonth && term.dayOfDueDate > 0 && momentDate.diff(toMoment(date), 'days') < term.dayOfDueDate) {
    momentDate = momentDate.add(term?.dayOfDueDate || 0, 'days');
    momentDate = momentDate.set('date', term?.dayOfMonth);
  }

  return momentDate.unix();
}

export function partStatusColor(status: ProductStatus) {
  switch (status) {
    case ProductStatus.returned:
      return 'rgba(244, 106, 106, 0.25)';
    case ProductStatus.partial:
      return 'rgba(116, 120, 141, 0.25)';
    case ProductStatus.ordered:
      return 'rgba(85, 110, 230, 0.25)';
    case ProductStatus.installed:
      return 'rgba(52, 195, 143, 0.25)';
    default:
      return '';
  }
}

export function getCOStatusClass(status: TMChangeOrderStatus) {
  switch (status) {
    case TMChangeOrderStatus.open:
      return 'badge-soft-info';
    case TMChangeOrderStatus.sent:
      return 'badge-soft-primary';
    case TMChangeOrderStatus.approved:
      return 'badge-soft-success';
    case TMChangeOrderStatus.paid:
      return 'badge-soft-success';
  }
}

export function shouldShowRMAItem(rmas: (CompactTimeMaterialRMAResponse | TimeMaterialRMAResponse)[], product: EstimateLineItem) {
  const productQuantity = product.quantity;
  const rmaTotal = R.filter(rmas, rma => rma.item == (product.id as any));
  return productQuantity > rmaTotal.length;
}

export function getFeeTotal(items: PriceFeeFormData[]) {
  const checkedItems = items.filter(item => item.isChecked || (item.isChecked as any) == 'true');
  const t = checkedItems.reduce((acc, item) => acc + R.toNumber(item.percent), 0);
  return t;
}

export function getFeeTotalAmount(items: PriceFeeFormData[], lineItems: OrderLineItemData[]) {
  const checkedItems = items.filter(item => item.isChecked || (item.isChecked as any) == 'true');
  const t = checkedItems.reduce((acc, item) => {
    const subTot = lineItems.reduce((car, o) => car + (ProposalService.getSubTotalAddProd(o, false, item.feeType) as number), 0);
    const fTotal = (subTot / 100) * item.percent;
    return acc + fTotal;
  }, 0);
  return t;
}

export function calculateTotalSalesTax(items: OrderLineItemData[], taxRate: number, fees: PriceFeeFormData[]) {
  const t = items.filter(i => i.taxable).reduce((acc, item) => acc + (ProposalService.getSubTotalAddProd(item, false) as number), 0);
  const taxableFeeTotalPercentage = fees.filter(i => i.isChecked && i.taxable).reduce((acc, item) => acc + item.percent, 0);
  const taxableFeeTotal = (t / 100) * taxableFeeTotalPercentage;
  const newT = t + taxableFeeTotal;
  return R.roundDecimal((newT / 100) * taxRate, 2);
}

export function tmFeeItems() {
  return [TMFeeType.item, TMFeeType.itemandlabor];
}

export function getTmFeeTypeLabel(type: TMFeeType) {
  switch (type) {
    case TMFeeType.item:
      return 'Items Only';
    case TMFeeType.itemandlabor:
      return 'Items & Labor';
  }
}

export function getOrderGTotal(order: TimeMaterialResponse) {
  const estimate = order.estimate;
  const laborPartsTotal = estimate.subTotal;
  const discount = getOrderDiscount(order);
  const salesTax = orderSalesTax(order);
  return laborPartsTotal + salesTax - discount;
}

export function getOrderDiscount(order: TimeMaterialResponse) {
  const estimate = order.estimate;
  const orderEstimateDiscount = estimate.discount as ProposalDiscountFormData;
  const discountValue = orderEstimateDiscount.value;
  const isDiscountByLabor = orderEstimateDiscount.category == ProposalDiscountCategory.labor;
  const isDiscountByItems = orderEstimateDiscount.category == ProposalDiscountCategory.items;

  if (!estimate.discount.isDiscount) return 0;

  if (estimate.discount.type === InvoiceDiscountType.fixed) {
    return estimate.discount.value;
  } else {
    if (isDiscountByLabor) {
      return R.toNumber(ProposalService.getDiscountAmount(orderEstimateDiscount, order.estimate.labourPrice));
    }
    if (isDiscountByItems) {
      return R.toNumber(ProposalService.getDiscountAmount(orderEstimateDiscount, order.estimate.partsPrice));
    }
    return R.toNumber((order.estimate.totalAmount * discountValue) / 100);
  }
}

export function orderSalesTax(order: TimeMaterialResponse) {
  const estimate = order.estimate;
  if (estimate.salesTax.taxAmount && estimate.salesTax.taxAmount > 0) {
    return estimate.salesTax.taxAmount;
  }
  if (estimate.salesTax.value === 0) return 0;
  const taxTotal = estimate.items
    .filter(l => l.taxable === true || l.packageItems?.some(p => p.taxable === true))
    .reduce((total, line) => total + (ProposalService.getSubTotalEstimate(line, false, false, true) as number), 0);
  return (estimate.salesTax.value * taxTotal) / 100;
}

export function getStatementIconForType(type: string) {
  switch (type) {
    case 'workOrder':
      return 'fa fa-file-text text-primary';
    case 'changeOrderAdd':
      return 'fa fa-plus-circle text-success';
    case 'changeOrderRemove':
      return 'fa fa-minus-circle text-warning';
    case 'invoice':
      return 'fa fa-file-invoice text-info';
    case 'creditMemo':
      return 'fa fa-credit-card text-danger';
    case 'payment':
      return 'fa fa-money-check text-success';
    default:
      return 'fa fa-circle text-muted';
  }
}

export function getStatementStatusBadge(type: string) {
  switch (type) {
    case 'workOrder':
      return 'fa fa-file-text text-primary';
    case 'changeOrderAdd':
      return 'fa fa-plus-circle text-success';
    case 'changeOrderRemove':
      return 'fa fa-minus-circle text-warning';
    case 'invoice':
      return 'fa fa-file-invoice text-info';
    case 'creditMemo':
      return 'fa fa-credit-card text-danger';
    case 'payment':
      return 'fa fa-money-check text-success';
    default:
      return 'fa fa-circle text-muted';
  }
}

export function newCoDiscountPercentage(
  order: TimeMaterialResponse,
  sOrder: TimeMaterialResponse | CompactTimeMaterialResponse,
  discount: number,
  formValue: any,
) {
  if (!order || !order.estimate) return null;

  const orderData = sOrder || order;

  const originalSubTotal = orderData.subTotal || 0;
  const originalDiscountTotal = orderData.discountTotal || 0;

  const changeOrderSubTotal = formValue.lineItems.reduce((total, line) => {
    // For newly added items (isEditable and no lineId), use quantity instead of changeQantity
    const isNewItem = line.isEditable && !line.lineId;
    const qty = isNewItem ? line.quantity || 0 : line.changeQantity || 0;
    const rate = line.rate || 0;
    const lineTotal = qty * rate;

    const packageTotal = line.packageItems.reduce((pkgTotal, pkg) => {
      const isNewPkgItem = pkg.isEditable && !pkg.lineId;
      const pkgQty = isNewPkgItem ? pkg.quantity || 0 : pkg.changeQantity || 0;
      const pkgRate = pkg.rate || 0;
      return pkgTotal + pkgQty * pkgRate;
    }, 0);

    return total + lineTotal + packageTotal;
  }, 0);

  const changeOrderDiscount = formValue.discount.isDiscount ? discount : 0;

  const newSubTotal = originalSubTotal + changeOrderSubTotal;
  // When change order subtotal is negative (removing items), subtract the discount
  // When positive (adding items), add the discount
  const newDiscountTotal =
    changeOrderSubTotal < 0 ? originalDiscountTotal - Math.abs(changeOrderDiscount) : originalDiscountTotal + Math.abs(changeOrderDiscount);

  if (newSubTotal === 0) return null;

  const newPercentage = (newDiscountTotal / newSubTotal) * 100;

  if (newDiscountTotal === 0 || Math.abs(newPercentage) < 0.01) return null;

  return newPercentage;
}
